import { remapComposerContextAttachments } from "@heydex/shared/composerContextReferences";
import {
  CommandId,
  CheckpointId,
  CheckpointScopeId,
  ORCHESTRATION_V2_WS_METHODS,
  OrchestrationV2CheckpointUnavailableError,
  WS_METHODS,
  type ChatAttachment,
  type MessageId,
  type ModelSelection,
  type OrchestrationV2Command,
  type OrchestrationV2CreationSource,
  type ProjectId,
  type ProjectIconOverride,
  type ProviderApprovalDecision,
  type ProviderUserInputAnswers,
  type RunId,
  type RuntimeMode,
  type RuntimeRequestId,
  type ThreadId,
  type UploadChatAttachment,
} from "@heydex/contracts";
import { derivePendingBackgroundWork } from "@heydex/shared/orchestrationV2PendingBackgroundWork";
import * as Crypto from "effect/Crypto";
import * as Effect from "effect/Effect";

import { request } from "../rpc/client.ts";

interface CommandMetadata {
  readonly commandId?: CommandId;
  readonly createdAt?: string;
  readonly creationSource?: OrchestrationV2CreationSource;
}

export interface CreateProjectInput extends CommandMetadata {
  readonly projectId: ProjectId;
  readonly title: string;
  readonly workspaceRoot: string;
  readonly createWorkspaceRootIfMissing?: boolean;
  readonly defaultModelSelection?: ModelSelection | null;
}

export interface UpdateProjectInput extends CommandMetadata {
  readonly projectId: ProjectId;
  readonly title?: string;
  readonly workspaceRoot?: string;
  readonly defaultModelSelection?: ModelSelection | null;
  readonly projectIcon?: ProjectIconOverride | null;
  readonly faviconPath?: string | null;
}

export interface DeleteProjectInput extends CommandMetadata {
  readonly projectId: ProjectId;
  readonly force?: boolean;
}

export interface CreateThreadInput extends CommandMetadata {
  readonly threadId: ThreadId;
  readonly projectId: ProjectId;
  readonly title: string;
  readonly modelSelection: ModelSelection;
  readonly runtimeMode: RuntimeMode;
}

export interface ThreadCommandInput extends CommandMetadata {
  readonly threadId: ThreadId;
}

export type DeleteThreadInput = ThreadCommandInput;
export type ArchiveThreadInput = ThreadCommandInput;
export type UnarchiveThreadInput = ThreadCommandInput;
export type SettleThreadInput = ThreadCommandInput;

export interface UnsettleThreadInput extends ThreadCommandInput {
  readonly reason: "user";
}

export interface PinThreadInput extends ThreadCommandInput {
  /** Initial slot in the user-arranged pinned order; omit to pin keyless. */
  readonly orderKey?: string;
}
export type UnpinThreadInput = ThreadCommandInput;

export interface ReorderPinnedThreadInput extends ThreadCommandInput {
  /** Fractional-index key that sorts between the drop position's neighbors. */
  readonly orderKey: string;
}

export interface ReorderActiveThreadInput extends ThreadCommandInput {
  /** Fractional-index key that sorts between the drop position's neighbors. */
  readonly orderKey: string;
}

export interface SnoozeThreadInput extends ThreadCommandInput {
  readonly snoozedUntil: string;
}

export interface UnsnoozeThreadInput extends ThreadCommandInput {
  readonly reason: "user";
}

export interface VisitThreadInput extends ThreadCommandInput {
  /** Watermark of the thread state the viewer has seen (ISO timestamp). */
  readonly visitedAt: string;
}

export type MarkThreadUnreadInput = ThreadCommandInput;

export interface UpdateThreadMetadataInput extends ThreadCommandInput {
  readonly limitRecovery?: import("@heydex/contracts").OrchestrationV2LimitRecoveryUpdate | null;
  readonly title?: string;
  readonly modelSelection?: ModelSelection;
  /** Kick off an async title regeneration for the thread. */
  readonly regenerateTitle?: boolean;
}

export interface SetThreadRuntimeModeInput extends ThreadCommandInput {
  readonly runtimeMode: RuntimeMode;
}

interface StartThreadBootstrap {
  readonly createThread?: {
    readonly projectId: ProjectId;
    readonly title: string;
    readonly modelSelection: ModelSelection;
    readonly runtimeMode: RuntimeMode;
    readonly createdAt: string;
  };
}

export interface StartThreadTurnInput extends ThreadCommandInput {
  readonly manualContinuationOfRunId?: RunId;
  readonly message: {
    readonly messageId: MessageId;
    readonly role: "user";
    readonly text: string;
    readonly attachments: ReadonlyArray<ChatAttachment | UploadChatAttachment>;
    readonly context?: import("@heydex/contracts").OrchestrationMessageContext;
  };
  readonly modelSelection?: ModelSelection;
  readonly titleSeed?: string;
  readonly runtimeMode: RuntimeMode;
  readonly bootstrap?: StartThreadBootstrap;
  readonly dispatchMode?: "auto" | "queue" | "steer" | "restart" | "start";
}

export interface InterruptThreadTurnInput extends ThreadCommandInput {
  readonly runId?: RunId;
  /** Temporary caller compatibility while UI naming moves from turns to runs. */
  readonly turnId?: string;
}

export interface RespondToThreadApprovalInput extends ThreadCommandInput {
  readonly requestId: RuntimeRequestId;
  readonly decision: ProviderApprovalDecision;
}

export interface RespondToThreadUserInputInput extends ThreadCommandInput {
  readonly requestId: RuntimeRequestId;
  readonly answers: ProviderUserInputAnswers;
  readonly attachmentsByQuestionId?: import("@heydex/contracts").UserInputAttachments;
}

export interface DismissThreadUserInputInput extends ThreadCommandInput {
  readonly requestId: RuntimeRequestId;
}

export interface RevertThreadCheckpointInput extends ThreadCommandInput {
  readonly checkpointId?: string;
  readonly scopeId?: string;
  readonly turnCount?: number;
}

export type StopThreadSessionInput = ThreadCommandInput;

export interface ForkThreadFromRunInput extends CommandMetadata {
  readonly sourceThreadId: ThreadId;
  readonly targetThreadId: ThreadId;
  readonly runId: RunId;
  readonly title?: string;
}

export interface ReorderQueuedRunInput extends ThreadCommandInput {
  readonly runId: RunId;
  readonly beforeRunId: RunId | null;
}

export interface PromoteQueuedRunInput extends ThreadCommandInput {
  readonly queuedRunId: RunId;
  readonly targetRunId: RunId;
}

export interface CancelQueuedRunInput extends ThreadCommandInput {
  readonly runId: RunId;
}

export interface RetryWorkspacePreparationInput extends ThreadCommandInput {
  readonly runId: RunId;
}

export interface EditQueuedRunInput extends ThreadCommandInput {
  readonly runId: RunId;
  readonly text: string;
  /**
   * Full replacement attachment list for the queued message. Omitted =
   * text-only edit that leaves attachments untouched. `dataUrl` entries are
   * persisted against `messageId` (the queued run's user message) before
   * dispatch, so `messageId` is required whenever attachments are present.
   */
  readonly edit?: {
    readonly messageId: MessageId;
    readonly attachments: ReadonlyArray<ChatAttachment | UploadChatAttachment>;
    readonly context?: import("@heydex/contracts").OrchestrationMessageContext;
  };
}

const allocateCommandId = Effect.fn("EnvironmentCommands.allocateCommandId")(function* (
  input: CommandMetadata,
) {
  if (input.commandId !== undefined) return input.commandId;
  const crypto = yield* Crypto.Crypto;
  return CommandId.make(yield* crypto.randomUUIDv4().pipe(Effect.orDie));
});

const dispatch = (command: OrchestrationV2Command) =>
  request(ORCHESTRATION_V2_WS_METHODS.dispatchCommand, command);

const getProjection = (threadId: ThreadId) =>
  request(ORCHESTRATION_V2_WS_METHODS.getThreadProjection, { threadId });

const persistAttachments = Effect.fn("EnvironmentCommands.persistAttachments")(function* (
  threadId: ThreadId,
  messageId: MessageId,
  attachments: ReadonlyArray<ChatAttachment | UploadChatAttachment>,
) {
  const stored = attachments.filter(
    (attachment): attachment is ChatAttachment => !("dataUrl" in attachment),
  );
  const uploads = attachments.filter(
    (attachment): attachment is UploadChatAttachment => "dataUrl" in attachment,
  );
  if (uploads.length === 0) return stored;
  const result = yield* request(WS_METHODS.assetsPersistChatAttachments, {
    threadId,
    messageId,
    attachments: uploads,
  });
  if (stored.length === 0) return result.attachments;
  const byUpload = new Map(
    uploads.map((attachment, index) => [attachment, result.attachments[index]]),
  );
  return attachments.flatMap((attachment) => {
    if (!("dataUrl" in attachment)) return [attachment];
    const persisted = byUpload.get(attachment);
    return persisted === undefined ? [] : [persisted];
  });
});

const mutateProject = Effect.fn("EnvironmentCommands.mutateProject")(function* (
  mutation:
    | {
        readonly type: "project.create";
        readonly commandId: CommandId;
        readonly projectId: ProjectId;
        readonly title: string;
        readonly workspaceRoot: string;
        readonly createWorkspaceRootIfMissing?: boolean;
        readonly defaultModelSelection?: ModelSelection | null;
      }
    | {
        readonly type: "project.update";
        readonly commandId: CommandId;
        readonly projectId: ProjectId;
        readonly title?: string;
        readonly workspaceRoot?: string;
        readonly defaultModelSelection?: ModelSelection | null;
      }
    | {
        readonly type: "project.delete";
        readonly commandId: CommandId;
        readonly projectId: ProjectId;
        readonly force?: boolean;
      },
) {
  return yield* request(WS_METHODS.projectsMutate, mutation);
});

export const createProject = Effect.fn("EnvironmentCommands.createProject")(function* (
  input: CreateProjectInput,
) {
  return yield* mutateProject({
    type: "project.create",
    commandId: yield* allocateCommandId(input),
    projectId: input.projectId,
    title: input.title,
    workspaceRoot: input.workspaceRoot,
    ...(input.createWorkspaceRootIfMissing === undefined
      ? {}
      : { createWorkspaceRootIfMissing: input.createWorkspaceRootIfMissing }),
    ...(input.defaultModelSelection === undefined
      ? {}
      : { defaultModelSelection: input.defaultModelSelection }),
  });
});

export const updateProject = Effect.fn("EnvironmentCommands.updateProject")(function* (
  input: UpdateProjectInput,
) {
  return yield* mutateProject({
    type: "project.update",
    commandId: yield* allocateCommandId(input),
    projectId: input.projectId,
    ...(input.title === undefined ? {} : { title: input.title }),
    ...(input.workspaceRoot === undefined ? {} : { workspaceRoot: input.workspaceRoot }),
    ...(input.defaultModelSelection === undefined
      ? {}
      : { defaultModelSelection: input.defaultModelSelection }),
    ...(input.projectIcon === undefined ? {} : { projectIcon: input.projectIcon }),
    ...(input.faviconPath === undefined ? {} : { faviconPath: input.faviconPath }),
  });
});

export const deleteProject = Effect.fn("EnvironmentCommands.deleteProject")(function* (
  input: DeleteProjectInput,
) {
  return yield* mutateProject({
    type: "project.delete",
    commandId: yield* allocateCommandId(input),
    projectId: input.projectId,
    ...(input.force === undefined ? {} : { force: input.force }),
  });
});

export const createThread = Effect.fn("EnvironmentCommands.createThread")(function* (
  input: CreateThreadInput,
) {
  return yield* dispatch({
    type: "thread.create",
    commandId: yield* allocateCommandId(input),
    createdBy: "user",
    creationSource: input.creationSource ?? "web",
    threadId: input.threadId,
    projectId: input.projectId,
    title: input.title,
    modelSelection: input.modelSelection,
    runtimeMode: input.runtimeMode,
    interactionMode: "default",
    worktreePath: null,
  });
});

function simpleThreadCommand(
  type:
    | "thread.delete"
    | "thread.archive"
    | "thread.unarchive"
    | "thread.settle"
    | "thread.pin"
    | "thread.unpin",
  input: ThreadCommandInput,
) {
  return allocateCommandId(input).pipe(
    Effect.flatMap((commandId) => dispatch({ type, commandId, threadId: input.threadId })),
  );
}

export const deleteThread = Effect.fn("EnvironmentCommands.deleteThread")(function* (
  input: DeleteThreadInput,
) {
  return yield* simpleThreadCommand("thread.delete", input);
});

export const archiveThread = Effect.fn("EnvironmentCommands.archiveThread")(function* (
  input: ArchiveThreadInput,
) {
  return yield* simpleThreadCommand("thread.archive", input);
});

export const unarchiveThread = Effect.fn("EnvironmentCommands.unarchiveThread")(function* (
  input: UnarchiveThreadInput,
) {
  return yield* simpleThreadCommand("thread.unarchive", input);
});

export const settleThread = Effect.fn("EnvironmentCommands.settleThread")(function* (
  input: SettleThreadInput,
) {
  return yield* simpleThreadCommand("thread.settle", input);
});

export const pinThread = Effect.fn("EnvironmentCommands.pinThread")(function* (
  input: PinThreadInput,
) {
  const commandId = yield* allocateCommandId(input);
  return yield* dispatch({
    type: "thread.pin",
    commandId,
    threadId: input.threadId,
    ...(input.orderKey === undefined ? {} : { orderKey: input.orderKey }),
  });
});

export interface SetThreadAutoSettleInput extends ThreadCommandInput {
  readonly enabled: boolean;
}
export const setThreadAutoSettle = Effect.fn("EnvironmentCommands.setThreadAutoSettle")(function* (
  input: SetThreadAutoSettleInput,
) {
  return yield* dispatch({
    type: "thread.auto-settle.set",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    enabled: input.enabled,
  });
});

export const reorderPinnedThread = Effect.fn("EnvironmentCommands.reorderPinnedThread")(function* (
  input: ReorderPinnedThreadInput,
) {
  const commandId = yield* allocateCommandId(input);
  return yield* dispatch({
    type: "thread.pin.reorder",
    commandId,
    threadId: input.threadId,
    orderKey: input.orderKey,
  });
});

export const reorderActiveThread = Effect.fn("EnvironmentCommands.reorderActiveThread")(function* (
  input: ReorderActiveThreadInput,
) {
  return yield* dispatch({
    type: "thread.active.reorder",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    orderKey: input.orderKey,
  });
});

export const unpinThread = Effect.fn("EnvironmentCommands.unpinThread")(function* (
  input: UnpinThreadInput,
) {
  return yield* simpleThreadCommand("thread.unpin", input);
});

export const unsettleThread = Effect.fn("EnvironmentCommands.unsettleThread")(function* (
  input: UnsettleThreadInput,
) {
  return yield* dispatch({
    type: "thread.unsettle",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    reason: input.reason,
  });
});

export const snoozeThread = Effect.fn("EnvironmentCommands.snoozeThread")(function* (
  input: SnoozeThreadInput,
) {
  return yield* dispatch({
    type: "thread.snooze",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    snoozedUntil: input.snoozedUntil,
  });
});

export const unsnoozeThread = Effect.fn("EnvironmentCommands.unsnoozeThread")(function* (
  input: UnsnoozeThreadInput,
) {
  return yield* dispatch({
    type: "thread.unsnooze",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    reason: input.reason,
  });
});

export const visitThread = Effect.fn("EnvironmentCommands.visitThread")(function* (
  input: VisitThreadInput,
) {
  return yield* dispatch({
    type: "thread.visit",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    visitedAt: input.visitedAt,
  });
});

export const markThreadUnread = Effect.fn("EnvironmentCommands.markThreadUnread")(function* (
  input: MarkThreadUnreadInput,
) {
  return yield* dispatch({
    type: "thread.mark-unread",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
  });
});

export const updateThreadMetadata = Effect.fn("EnvironmentCommands.updateThreadMetadata")(
  function* (input: UpdateThreadMetadataInput) {
    const commandId = yield* allocateCommandId(input);
    let result = null;
    if (
      input.title !== undefined ||
      input.regenerateTitle !== undefined ||
      input.limitRecovery !== undefined
    ) {
      result = yield* dispatch({
        type: "thread.metadata.update",
        ...(input.limitRecovery === undefined ? {} : { limitRecovery: input.limitRecovery }),
        commandId,
        threadId: input.threadId,
        ...(input.title === undefined ? {} : { title: input.title }),
        ...(input.regenerateTitle === undefined ? {} : { regenerateTitle: input.regenerateTitle }),
      });
    }
    if (input.modelSelection !== undefined) {
      result = yield* dispatch({
        type: "thread.model-selection.set",
        commandId: result === null ? commandId : CommandId.make(`${commandId}:model-selection`),
        threadId: input.threadId,
        modelSelection: input.modelSelection,
      });
    }
    return result ?? { sequence: 0 };
  },
);

export const setThreadRuntimeMode = Effect.fn("EnvironmentCommands.setThreadRuntimeMode")(
  function* (input: SetThreadRuntimeModeInput) {
    return yield* dispatch({
      type: "thread.runtime-mode.set",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      runtimeMode: input.runtimeMode,
    });
  },
);

export const startThreadTurn = Effect.fn("EnvironmentCommands.startThreadTurn")(function* (
  input: StartThreadTurnInput,
) {
  const commandId = yield* allocateCommandId(input);
  const attachments = yield* persistAttachments(
    input.threadId,
    input.message.messageId,
    input.message.attachments,
  );
  const context = remapComposerContextAttachments(
    input.message.context,
    input.message.attachments,
    attachments,
  );
  const bootstrap = input.bootstrap?.createThread;
  if (bootstrap !== undefined) {
    const thread = bootstrap;
    return yield* request(ORCHESTRATION_V2_WS_METHODS.launchThread, {
      commandId,
      creationSource: input.creationSource ?? "web",
      threadId: input.threadId,
      projectId: thread.projectId,
      title: input.titleSeed ?? thread.title,
      generateTitle: input.titleSeed !== undefined,
      modelSelection: input.modelSelection ?? thread.modelSelection,
      runtimeMode: input.runtimeMode,
      interactionMode: "default",
      workspaceStrategy: { type: "root" },
      initialMessage: {
        messageId: input.message.messageId,
        text: input.message.text,
        ...(context ? { context } : {}),
        attachments,
      },
    });
  }

  const requestedMode = input.dispatchMode ?? "auto";
  if (requestedMode === "start") {
    return yield* dispatch({
      type: "message.dispatch",
      commandId,
      createdBy: "user",
      creationSource: input.creationSource ?? "web",
      threadId: input.threadId,
      messageId: input.message.messageId,
      ...(input.manualContinuationOfRunId === undefined
        ? {}
        : { manualContinuationOfRunId: input.manualContinuationOfRunId }),
      text: input.message.text,
      ...(context ? { context } : {}),
      attachments,
      ...(input.modelSelection === undefined ? {} : { modelSelection: input.modelSelection }),
      dispatchMode: { type: "start_immediately" },
    });
  }

  return yield* dispatch({
    type: "message.dispatch",
    commandId,
    createdBy: "user",
    creationSource: input.creationSource ?? "web",
    threadId: input.threadId,
    messageId: input.message.messageId,
    text: input.message.text,
    ...(context ? { context } : {}),
    attachments,
    ...(input.titleSeed === undefined ? {} : { titleSeed: input.titleSeed }),
    ...(input.modelSelection === undefined ? {} : { modelSelection: input.modelSelection }),
    ...(requestedMode === "queue" ? {} : { deliveryIntent: requestedMode }),
    dispatchMode:
      requestedMode === "queue" ? { type: "queue_after_active" } : { type: "start_immediately" },
  });
});

/**
 * Stop for the thread's latest work: interrupts the active run, or the settled run whose
 * background work still runs. With no run to stop, it does nothing.
 */
export const interruptThreadTurn = Effect.fn("EnvironmentCommands.interruptThreadTurn")(function* (
  input: InterruptThreadTurnInput,
) {
  let runId = input.runId ?? (input.turnId as RunId | undefined);
  if (runId === undefined) {
    const projection = yield* getProjection(input.threadId);
    runId = projection.runs.findLast(
      (run) =>
        run.status === "preparing" ||
        run.status === "starting" ||
        run.status === "running" ||
        run.status === "waiting",
    )?.id;
    if (runId === undefined) {
      const latestRun = projection.runs.at(-1);
      if (
        derivePendingBackgroundWork({
          latestRun,
          providerThreads: projection.providerThreads,
          turnItems: projection.turnItems,
          activeProviderThreadId: projection.thread.activeProviderThreadId,
          runs: projection.runs,
        }).length > 0
      ) {
        runId = latestRun?.id;
      }
    }
  }
  if (runId === undefined) return { sequence: 0 };
  return yield* dispatch({
    type: "run.interrupt",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    runId,
    holdQueue: true,
  });
});

export const respondToThreadApproval = Effect.fn("EnvironmentCommands.respondToThreadApproval")(
  function* (input: RespondToThreadApprovalInput) {
    return yield* dispatch({
      type: "runtime-request.respond",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      requestId: input.requestId,
      decision: input.decision,
    });
  },
);

export const respondToThreadUserInput = Effect.fn("EnvironmentCommands.respondToThreadUserInput")(
  function* (input: RespondToThreadUserInputInput) {
    return yield* dispatch({
      type: "runtime-request.respond",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      requestId: input.requestId,
      answers: input.answers,
      ...(input.attachmentsByQuestionId === undefined
        ? {}
        : { attachmentsByQuestionId: input.attachmentsByQuestionId }),
    });
  },
);

export const dismissThreadUserInput = Effect.fn("EnvironmentCommands.dismissThreadUserInput")(
  function* (input: DismissThreadUserInputInput) {
    return yield* dispatch({
      type: "thread.user-input.dismiss",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      requestId: input.requestId,
    });
  },
);

export const revertThreadCheckpoint = Effect.fn("EnvironmentCommands.revertThreadCheckpoint")(
  function* (input: RevertThreadCheckpointInput) {
    if (input.checkpointId !== undefined && input.scopeId !== undefined) {
      return yield* dispatch({
        type: "checkpoint.rollback",
        commandId: yield* allocateCommandId(input),
        threadId: input.threadId,
        scopeId: CheckpointScopeId.make(input.scopeId),
        checkpointId: CheckpointId.make(input.checkpointId),
      });
    }
    const projection = yield* getProjection(input.threadId);
    const checkpoint =
      projection.checkpoints.find(
        (candidate) => candidate.id === input.checkpointId && candidate.scopeId === input.scopeId,
      ) ??
      projection.checkpoints.findLast((candidate) =>
        input.turnCount === 0
          ? candidate.ordinalWithinScope === 0 && candidate.appRunOrdinal === null
          : candidate.appRunOrdinal === input.turnCount,
      );
    if (checkpoint === undefined) {
      const target =
        input.checkpointId === undefined
          ? `run ordinal ${input.turnCount ?? "unknown"}`
          : `checkpoint ${input.checkpointId}`;
      return yield* new OrchestrationV2CheckpointUnavailableError({
        threadId: input.threadId,
        target,
      });
    }
    return yield* dispatch({
      type: "checkpoint.rollback",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      scopeId: checkpoint.scopeId,
      checkpointId: checkpoint.id,
    });
  },
);

export const stopThreadSession = Effect.fn("EnvironmentCommands.stopThreadSession")(function* (
  input: StopThreadSessionInput,
) {
  const projection = yield* getProjection(input.threadId);
  const commandId = yield* allocateCommandId(input);
  let result = { sequence: 0 };
  for (const session of projection.providerSessions) {
    result = yield* dispatch({
      type: "provider-session.detach",
      commandId: CommandId.make(`${commandId}:detach:${session.id}`),
      threadId: input.threadId,
      providerSessionId: session.id,
      reason: "client-requested",
    });
  }
  return result;
});

export const forkThreadFromRun = Effect.fn("EnvironmentCommands.forkThreadFromRun")(function* (
  input: ForkThreadFromRunInput,
) {
  return yield* dispatch({
    type: "thread.fork",
    commandId: yield* allocateCommandId(input),
    createdBy: "user",
    creationSource: input.creationSource ?? "web",
    sourceThreadId: input.sourceThreadId,
    targetThreadId: input.targetThreadId,
    sourcePoint: { type: "run", runId: input.runId },
    ...(input.title === undefined ? {} : { title: input.title }),
  });
});

export const resumeThreadQueue = Effect.fn("EnvironmentCommands.resumeThreadQueue")(function* (
  input: ThreadCommandInput,
) {
  return yield* dispatch({
    type: "queue.resume",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
  });
});

export const reorderQueuedRun = Effect.fn("EnvironmentCommands.reorderQueuedRun")(function* (
  input: ReorderQueuedRunInput,
) {
  return yield* dispatch({
    type: "queued-run.reorder",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    runId: input.runId,
    beforeRunId: input.beforeRunId,
  });
});

export const promoteQueuedRun = Effect.fn("EnvironmentCommands.promoteQueuedRun")(function* (
  input: PromoteQueuedRunInput,
) {
  return yield* dispatch({
    type: "queued-message.promote-to-steer",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    queuedRunId: input.queuedRunId,
    targetRunId: input.targetRunId,
  });
});

export const cancelQueuedRun = Effect.fn("EnvironmentCommands.cancelQueuedRun")(function* (
  input: CancelQueuedRunInput,
) {
  return yield* dispatch({
    type: "queued-run.cancel",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    runId: input.runId,
  });
});

export const retryWorkspacePreparation = Effect.fn("EnvironmentCommands.retryWorkspacePreparation")(
  function* (input: RetryWorkspacePreparationInput) {
    return yield* dispatch({
      type: "prepared-run.retry",
      commandId: yield* allocateCommandId(input),
      threadId: input.threadId,
      runId: input.runId,
    });
  },
);

export const editQueuedRun = Effect.fn("EnvironmentCommands.editQueuedRun")(function* (
  input: EditQueuedRunInput,
) {
  const attachments =
    input.edit === undefined
      ? undefined
      : yield* persistAttachments(input.threadId, input.edit.messageId, input.edit.attachments);
  return yield* dispatch({
    type: "queued-run.edit",
    commandId: yield* allocateCommandId(input),
    threadId: input.threadId,
    runId: input.runId,
    text: input.text,
    ...(attachments === undefined ? {} : { attachments }),
    ...(input.edit?.context && attachments
      ? {
          context: remapComposerContextAttachments(
            input.edit.context,
            input.edit.attachments,
            attachments,
          ),
        }
      : {}),
  });
});
